/**
 * BPMN lane/pool rail edit API (S-lane-edit) — the WYSIWYG lane-container
 * gestures translated to view-block flow-body mutations. A lane is a projection
 * of its steps' owner, so CONTAINER edits (add / rename / remove / reorder a
 * lane or pool) rewrite the `flow X bpmn { lane/pool }` clause list; they never
 * touch step ownership. The pure band→clause correlation the engine emits
 * (`sourceClauseIndex` / `editable` / `ownerModule`) decides which gestures are
 * offered — this module only maps a chosen gesture to one typed mutation.
 *
 * BpmnView consumes {@link BpmnLaneEditApi} (band-keyed, UI-facing); the
 * factory binds it to the mutation service + the target view/flow coordinates
 * (FlowContainer owns those). Kept out of the view component so BpmnView stays
 * a pure renderer.
 */
import type { BpmnLaneBand, BpmnPoolBand, WalkStep } from "@archlang/engine";
import { CH, PAD } from "@archlang/scene/process";
import type { ModelMutationsApi } from "../workbench/mutations.js";
import { flowBodyRowText, type FlowBodyRow } from "../views/clauseModel.js";

/** The lane band whose track range contains a rail-local pixel `y` (headroom
 *  already subtracted), or undefined outside every band. Pure — the step-drag
 *  drop resolver, unit-tested independent of DOM geometry. */
export function bandAtY(lanes: readonly BpmnLaneBand[], y: number): BpmnLaneBand | undefined {
  for (const b of lanes) {
    if (y >= PAD + b.y0 * CH && y < PAD + (b.y1 + 1) * CH) return b;
  }
  return undefined;
}

/** Band-keyed lane/pool editing surfaced on the rail. Every method is a no-op
 *  when the band carries no editable source clause (the UI hides the affordance
 *  too — this is defense in depth). */
export interface BpmnLaneEditApi {
  /** Rename a lane, preserving its selector (retitle). Editable bands only. */
  renameLane(band: BpmnLaneBand, title: string): void;
  /** Remove a lane's clause (its steps fall to the default lane). */
  removeLane(band: BpmnLaneBand): void;
  /** Nudge a lane's clause one slot earlier/later in the body order. */
  reorderLane(band: BpmnLaneBand, dir: "up" | "down"): void;
  /** Append a new `lane <ref>` for a bare module ref. */
  addLane(moduleRef: string): void;
  /** Rename a pool, preserving its selector (pool-merge only). */
  renamePool(pool: BpmnPoolBand, title: string): void;
  /** Remove a pool's clause (its lanes un-pool). */
  removePool(pool: BpmnPoolBand): void;
  /** Append a new `pool <ref>` for a bare module ref. */
  addPool(moduleRef: string): void;
  /**
   * Reassign a step (by its walker `stepIndex`) to a target lane's owner module
   * — an OWNER model-edit (`reassignStepOwner`), NOT a view-block override: a
   * lane is a projection of the step's owner, so the derived lane follows. Works
   * for a plain outbound call ANYWHERE the engine resolver finds a rewritable
   * owner span — top-level, nested (branch/switch/try/each/parallel/do), or a
   * chain head. Returns true when a write was issued. */
  reassignStep(stepIndex: number, targetOwnerModule: string): boolean;
  /**
   * Whether a step's owner can be reassigned at all (a plain call whose owner
   * the engine resolver can rewrite) — the drop affordance is shown ENABLED
   * only for these; every other real step shows a disabled drop + tooltip,
   * never a silent no-op. Independent of any particular target lane. */
  canReassignStep(stepIndex: number): boolean;
  /**
   * True on the PROCESS-tab viewer, where there is no view to hold lane/pool
   * CONFIG clauses (add/rename/merge/pool) — only step→lane reassignment is a
   * process-truth edit. The rail then hides its container affordances (the
   * "+ lane / + pool" row); the step-drag path stays live. Absent/false on a
   * bpmn VIEW surface, which owns the full config. */
  readonly reassignOnly?: boolean;
}

/** Whether a walk step reassigns cleanly — a plain outbound call with nothing a
 *  `from > to` span rewrite would drop. Fail-closed (the foreman guard). */
export function isPlainReassignable(ws: WalkStep | undefined): ws is WalkStep {
  return (
    !!ws &&
    ws.kind === "step" &&
    !ws.noteText &&
    !ws.prose &&
    !ws.bind &&
    (!ws.args || ws.args.length === 0) &&
    !ws.asName &&
    !ws.reentryTarget &&
    !ws.comment &&
    !ws.fromInstance &&
    !ws.toInstance &&
    !ws.toAnonymous &&
    ws.direction !== "inbound"
  );
}

/** A bare / dotted module reference — the only shape we author into a fresh
 *  `lane`/`pool` clause. Anything else is dropped (the recompile invariant is
 *  the last guard, but we never knowingly write unparseable text). Segments
 *  mirror the LEXER's identifier shape — unicode idents + internal hyphens are
 *  legal module names (spec §5.1); ASCII-only silently refused them (r46). */
const REF_SEG = "[A-Za-z_$\\p{ID_Start}][\\w$\\p{ID_Continue}]*(?:-[\\w$\\p{ID_Continue}]+)*";
const REF = new RegExp(`^${REF_SEG}(\\.${REF_SEG})*$`, "u");

export function makeBpmnLaneEditApi(args: {
  readonly mutations: ModelMutationsApi;
  /** Registered view name (or `#id`) hosting the flow clause. */
  readonly viewName: string;
  /** Index of the flow clause within the view's clause list. */
  readonly flowClauseIndex: number;
  /** Body clause count — bounds reorder nudges. */
  readonly clauseCount: number;
  /** stepIndex → WalkStep, for the product-policy reassign gate. */
  readonly stepByIndex?: ReadonlyMap<number, WalkStep>;
  /** stepIndex → hop stepId key (engine owner-reassign resolver). Absent entry
   *  ⇒ owner not source-rewritable. */
  readonly reassignTargets?: ReadonlyMap<number, string>;
  /** mutateProcessStep target (`#id` or qualified name). */
  readonly processHandle?: string;
  /** Process-tab viewer: reassignment only, no view for container config. */
  readonly reassignOnly?: boolean;
}): BpmnLaneEditApi {
  const { mutations, viewName, flowClauseIndex, clauseCount, stepByIndex, reassignTargets, processHandle, reassignOnly } = args;
  const at = (idx: number | undefined): idx is number => idx !== undefined && idx >= 0;
  // A step reassigns iff (product policy) it's a plain outbound call AND (engine)
  // its owner has a source span the resolver can rewrite. Both gates, so a
  // bind/args/comment/instance step or an implicit-caller hop stays disabled.
  const reassignTargetFor = (stepIndex: number): string | undefined => {
    if (!stepByIndex || !reassignTargets || !processHandle) return undefined;
    if (!isPlainReassignable(stepByIndex.get(stepIndex))) return undefined;
    return reassignTargets.get(stepIndex);
  };

  return {
    ...(reassignOnly ? { reassignOnly: true as const } : {}),
    renameLane(band, title) {
      if (!at(band.sourceClauseIndex) || !band.editable || title.trim() === "") return;
      mutations.mutateFlowBody(viewName, flowClauseIndex, {
        op: "retitleClause",
        index: band.sourceClauseIndex,
        title: title.trim(),
      });
    },
    removeLane(band) {
      if (!at(band.sourceClauseIndex) || !band.editable) return;
      mutations.mutateFlowBody(viewName, flowClauseIndex, { op: "removeClause", index: band.sourceClauseIndex });
    },
    reorderLane(band, dir) {
      if (!at(band.sourceClauseIndex) || !band.editable) return;
      const to = band.sourceClauseIndex + (dir === "up" ? -1 : 1);
      if (to < 0 || to >= clauseCount) return;
      mutations.mutateFlowBody(viewName, flowClauseIndex, {
        op: "reorderClause",
        from: band.sourceClauseIndex,
        to,
      });
    },
    addLane(moduleRef) {
      const ref = moduleRef.trim();
      if (!REF.test(ref)) return;
      mutations.mutateFlowBody(viewName, flowClauseIndex, { op: "appendClause", clauseText: `lane ${ref}` });
    },
    renamePool(pool, title) {
      if (!at(pool.sourceClauseIndex) || !pool.editable || title.trim() === "") return;
      mutations.mutateFlowBody(viewName, flowClauseIndex, {
        op: "retitleClause",
        index: pool.sourceClauseIndex,
        title: title.trim(),
      });
    },
    removePool(pool) {
      if (!at(pool.sourceClauseIndex) || !pool.editable) return;
      mutations.mutateFlowBody(viewName, flowClauseIndex, { op: "removeClause", index: pool.sourceClauseIndex });
    },
    addPool(moduleRef) {
      const ref = moduleRef.trim();
      if (!REF.test(ref)) return;
      mutations.mutateFlowBody(viewName, flowClauseIndex, { op: "appendClause", clauseText: `pool ${ref}` });
    },
    reassignStep(stepIndex, targetOwnerModule) {
      if (!REF.test(targetOwnerModule)) return false;
      const stepId = reassignTargetFor(stepIndex);
      if (stepId === undefined) return false; // not reassignable — fail closed
      const from = stepByIndex!.get(stepIndex)!.from.join(".");
      if (from === targetOwnerModule) return false; // already there — no-op
      const res = mutations.mutateProcessStep(processHandle!, {
        op: "reassignStepOwner",
        stepId,
        owner: targetOwnerModule,
      });
      return res.status === "applied";
    },
    canReassignStep(stepIndex) {
      return reassignTargetFor(stepIndex) !== undefined;
    },
  };
}

/** Map a band's `sourceClauseIndex` (an index into the COMPLETE clauses the
 *  layout was built from) back to its index in the raw draft-row array —
 *  incomplete rows (no emitted text) are skipped in the layout but still occupy
 *  a slot in the array, so positional splicing needs this bridge. -1 = no match. */
function draftRowIndexOf(rows: readonly FlowBodyRow[], clauseIndex: number): number {
  let seen = -1;
  for (let i = 0; i < rows.length; i++) {
    if (flowBodyRowText(rows[i]!) !== null) {
      seen++;
      if (seen === clauseIndex) return i;
    }
  }
  return -1;
}

/**
 * The process-tab BPMN lane editor — the SAME rail gestures as
 * {@link makeBpmnLaneEditApi}, but the container edits mutate an EPHEMERAL draft
 * lane body (a `FlowBodyRow[]` held in the tab, browser-only) instead of writing
 * a model view. This is the process diagram's "it's a view with default
 * settings, editable to reason" tier — nothing is persisted until the user saves
 * it as a real view. Step→lane reassignment is unchanged: that's process truth
 * (who performs the step), a real `reassignStepOwner` model edit, so it delegates
 * to the model-backed factory verbatim.
 */
export function makeBpmnDraftLaneEditApi(args: {
  readonly rows: readonly FlowBodyRow[];
  readonly setRows: (next: readonly FlowBodyRow[]) => void;
  /** Reassign wiring (a real model edit). Absent ⇒ read-only viewer: lanes are
   *  still editable in-browser, but step→lane reassignment is disabled. */
  readonly mutations?: ModelMutationsApi;
  readonly stepByIndex?: ReadonlyMap<number, WalkStep>;
  readonly reassignTargets?: ReadonlyMap<number, string>;
  readonly processHandle?: string;
}): BpmnLaneEditApi {
  const { rows, setRows, mutations, stepByIndex, reassignTargets, processHandle } = args;
  // Reassignment is process truth — reuse the model-backed path (no view coords).
  // Without a mutation service it's inert (read-only viewer).
  const reassign: Pick<BpmnLaneEditApi, "reassignStep" | "canReassignStep"> = mutations
    ? makeBpmnLaneEditApi({
        mutations,
        viewName: "",
        flowClauseIndex: -1,
        clauseCount: 0,
        ...(stepByIndex ? { stepByIndex } : {}),
        ...(reassignTargets ? { reassignTargets } : {}),
        ...(processHandle ? { processHandle } : {}),
      })
    : { reassignStep: () => false, canReassignStep: () => false };
  const rowAt = (clauseIndex: number | undefined): number =>
    clauseIndex === undefined ? -1 : draftRowIndexOf(rows, clauseIndex);
  const splice = (i: number): void => setRows(rows.filter((_, j) => j !== i));
  const swap = (i: number, j: number): void => {
    if (i < 0 || j < 0 || i >= rows.length || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setRows(next);
  };
  const replace = (i: number, row: FlowBodyRow): void =>
    setRows(rows.map((r, j) => (j === i ? row : r)));

  return {
    renameLane(band, title) {
      const i = rowAt(band.sourceClauseIndex);
      const row = rows[i];
      if (i < 0 || !row || title.trim() === "") return;
      // Retitle keeps the row's selector; only the two selector-carrying lane
      // shapes have a title slot (a `lane by` value band has none).
      if (row.kind === "lane-expand" || row.kind === "lane-merge") {
        replace(i, { kind: "lane-merge", title: title.trim(), selector: row.selector });
      }
    },
    removeLane(band) {
      const i = rowAt(band.sourceClauseIndex);
      if (i >= 0) splice(i);
    },
    reorderLane(band, dir) {
      const i = rowAt(band.sourceClauseIndex);
      if (i >= 0) swap(i, i + (dir === "up" ? -1 : 1));
    },
    addLane(moduleRef) {
      const ref = moduleRef.trim();
      if (!REF.test(ref)) return;
      setRows([...rows, { kind: "lane-expand", selector: { kind: "element", path: ref } }]);
    },
    renamePool(pool, title) {
      const i = rowAt(pool.sourceClauseIndex);
      const row = rows[i];
      if (i < 0 || !row || title.trim() === "") return;
      if (row.kind === "pool-ref") {
        replace(i, { kind: "pool-merge", title: title.trim(), selector: { kind: "element", path: row.ref } });
      } else if (row.kind === "pool-merge") {
        replace(i, { kind: "pool-merge", title: title.trim(), selector: row.selector });
      }
    },
    removePool(pool) {
      const i = rowAt(pool.sourceClauseIndex);
      if (i >= 0) splice(i);
    },
    addPool(moduleRef) {
      const ref = moduleRef.trim();
      if (!REF.test(ref)) return;
      setRows([...rows, { kind: "pool-ref", ref }]);
    },
    reassignStep: reassign.reassignStep,
    canReassignStep: reassign.canReassignStep,
  };
}
